import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Text } from '@/components/Text';
import { SeatsModal } from '@/partials/dashboard/Pricing/SeatsModal';
import { subscribe } from '@/services/stripeService';
import { useAuthStore } from '@/stores/useAuthStore';
import i18n from '@/translations/config';
import { Plan } from '@/types/pricing';
import { cn } from '@/utils/cn';
import { handleError } from '@/utils/handleError';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosCheckmark } from 'react-icons/io';
import { IoArrowBackOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

// Plan data with pricing and IDs
const planData: Plan[] = [
  {
    type: 'freemium',
    id: 'freemium',
    monthly: 0,
    yearly: 0,
    monthly_plan_id: null,
    yearly_plan_id: null,
    disabled: true,
  },
  {
    type: 'plus',
    id: 'business-plus',
    monthly: 8.99,
    yearly: 7.49,
    monthly_plan_id: 1,
    yearly_plan_id: 2,
    disabled: false,
  },
  {
    type: 'pro',
    id: 'business-pro',
    monthly: 16.99,
    yearly: 13.49,
    monthly_plan_id: 3,
    yearly_plan_id: 4,
    disabled: true,
  },
  {
    id: 'custom',
    monthly: null,
    yearly: null,
    monthly_plan_id: null,
    yearly_plan_id: null,
    disabled: false,
  },
];

const Pricing = () => {
  const { t } = useTranslation('pricing');
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(true);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [isSeatsModalOpen, setIsSeatsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  // Find the active or trialing subscription
  const activeSubscription = user?.subscriptions?.find(
    sub => sub.stripe_status === 'active' || sub.stripe_status === 'trialing',
  );

  // Get the current plan type
  const currentPlanType = activeSubscription?.type?.toLowerCase() || 'freemium';

  const handleSubscribe = async (planId: string) => {
    if (planId === 'custom') {
      window.open('https://linko-connect.com/contact/', '_blank');
      return;
    }

    try {
      const plan = planData.find(p => p.id === planId);
      if (!plan?.monthly_plan_id || !plan?.yearly_plan_id) return;

      // Check if user is manager or admin and has a company
      if (
        (user?.role === 'manager' || user?.role === 'admin') &&
        user?.company?.id
      ) {
        // Show seats modal
        setSelectedPlan(plan);
        setIsSeatsModalOpen(true);
        return;
      }

      // For regular users, proceed directly to subscription
      await proceedToSubscription(plan);
    } catch (error) {
      handleError(error);
    }
  };

  const proceedToSubscription = async (plan: Plan, seats?: number | null) => {
    try {
      const planId = isYearly ? plan.yearly_plan_id : plan.monthly_plan_id;

      if (!planId) {
        return;
      }

      const { data } = await subscribe(
        planId,
        isYearly ? 'yearly' : 'monthly',
        seats ?? undefined,
      );

      window.location.href = data.checkout_url ?? data.portal;
    } catch (error) {
      handleError(error);
    }
  };

  const handleSeatsConfirm = (seats: number) => {
    if (selectedPlan) {
      proceedToSubscription(selectedPlan, seats);
      setIsSeatsModalOpen(false);
    }
  };

  const toggleAllFeatures = () => {
    setShowAllFeatures(!showAllFeatures);
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center overflow-x-hidden bg-white py-4">
      {selectedPlan !== null &&
        selectedPlan.monthly !== null &&
        selectedPlan.yearly !== null && (
          <SeatsModal
            isOpen={isSeatsModalOpen}
            onClose={() => setIsSeatsModalOpen(false)}
            onConfirm={handleSeatsConfirm}
            planName={t(`plans.${selectedPlan.id}.name`)}
            price={isYearly ? selectedPlan.yearly : selectedPlan.monthly}
            isYearly={isYearly}
          />
        )}

      {/* Back Button */}
      <div className="w-full max-w-7xl px-4 sm:px-6">
        <div
          className="mb-6 flex w-full items-center justify-start"
          onClick={() => navigate(`/${i18n.language}/dashboard`)}
        >
          <IoArrowBackOutline className="mr-2 size-6 cursor-pointer" />
        </div>
      </div>
      <div className="mx-auto w-full max-w-7xl flex-1 space-y-6 px-4 sm:px-6">
        {/* Header */}
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-3xl font-bold text-secondary">{t('title')}</h1>
          <Text className="mx-auto max-w-2xl text-center text-lg text-gray-600">
            {t('subtitle')}
          </Text>
        </div>

        {/* Toggle Monthly/Yearly */}
        <div className="flex items-center justify-center">
          <div className="relative flex h-14 w-[340px] overflow-hidden rounded-full border border-gray-200 bg-white shadow-sm">
            <motion.div
              layout
              initial={false}
              animate={{ x: isYearly ? 170 : 0 }}
              transition={{
                type: 'spring' as const,
                stiffness: 500,
                damping: 30,
              }}
              className="absolute left-0 top-0 z-0 h-full w-1/2 rounded-full bg-linko-dark shadow-md"
            />
            <button
              type="button"
              className={cn(
                'relative z-10 flex flex-1 items-center justify-center rounded-full text-xl font-bold transition-colors duration-300 focus:outline-none',
                isYearly ? 'text-linko-dark' : 'text-white',
              )}
              onClick={() => setIsYearly(false)}
            >
              {t('monthly')}
            </button>
            <button
              type="button"
              className={cn(
                'relative z-10 flex flex-1 items-center justify-center rounded-full text-xl font-bold transition-colors duration-300 focus:outline-none',
                isYearly ? 'text-white' : 'text-linko-dark',
              )}
              onClick={() => setIsYearly(true)}
            >
              {t('yearly')}
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {planData.map((plan, planIndex) => {
            const planId = plan.id;
            const isPaid = plan.monthly !== 0 && plan.monthly !== null;
            const isCustom = plan.monthly === null;
            const price = isYearly ? plan.yearly : plan.monthly;
            const isRecommended = planId === 'business-plus';
            const isDisabled = plan.disabled;
            const isCurrentPlan = plan.type === currentPlanType;

            // Get translated plan data
            const name = t(`plans.${planId}.name`);
            const label = t(`plans.${planId}.label`);
            const description = t(`plans.${planId}.description`);
            const cta = t(`plans.${planId}.cta`);
            const features = t(`plans.${planId}.features`, {
              returnObjects: true,
            }) as string[];
            const inheritanceText = t(`plans.${planId}.inheritance`);

            // Show first 4 features by default, all when expanded
            const displayedFeatures = showAllFeatures
              ? features
              : features.slice(0, 5);

            return (
              <motion.div
                key={planIndex}
                className={cn(
                  'relative flex flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition-all',
                  isCurrentPlan
                    ? 'border-green-400 shadow-green-200/30 ring-1 ring-green-400'
                    : isRecommended
                      ? 'border-linko-light shadow-linko-light/20'
                      : 'border-gray-200',
                )}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: planIndex * 0.1 }}
              >
                {/* Recommended Badge */}
                {label && (
                  <Badge label={label} color="blue" position="top-right" />
                )}

                {/* Discount Badge - only show for yearly billing */}
                {isYearly &&
                  (planId === 'business-plus' || planId === 'business-pro') && (
                    <Badge
                      label={`-${planId === 'business-plus' ? '16' : '18'}%`}
                      color="green"
                      position="top-left"
                    />
                  )}

                {/* Card Header */}
                <div className="mt-4 flex flex-col space-y-2 p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {name}
                    </h3>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{description}</p>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center">
                      {isCustom ? (
                        <span className="text-3xl font-bold text-secondary">
                          {t('customized')}
                        </span>
                      ) : (
                        <>
                          <span className="text-3xl font-bold text-secondary">
                            {price === 0 ? t('free') : `${price}€`}
                          </span>
                          {isPaid && (
                            <span className="ml-1 text-sm text-gray-500">
                              {isYearly
                                ? t('per-month-yearly')
                                : t('per-month')}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {!isYearly &&
                      ['business-plus', 'business-pro'].includes(planId) && (
                        <div className="mt-1 text-xs font-medium text-green-600">
                          {t('cheaper-yearly')}
                        </div>
                      )}
                  </div>
                </div>

                <Text className="mx-4 mt-1 text-sm font-medium text-linko-dark">
                  {planId !== 'custom' &&
                    planId !== 'freemium' &&
                    inheritanceText}
                </Text>
                {/* Feature List */}
                <div className="flex flex-1 flex-col justify-between px-2 pt-4">
                  <div className="space-y-3">
                    {displayedFeatures.map(
                      (feature: string, featureIndex: number) => (
                        <div key={featureIndex} className="flex items-start">
                          <IoIosCheckmark className="mr-2 mt-0.5 size-6 shrink-0 text-green-500" />
                          <span className="text-sm text-gray-700">
                            {feature}
                          </span>
                        </div>
                      ),
                    )}
                  </div>

                  {features.length > 4 && (
                    <button
                      onClick={toggleAllFeatures}
                      className="mt-4 text-sm font-medium text-linko-dark hover:text-linko-light"
                    >
                      {showAllFeatures ? t('show-less') : t('show-more')}
                    </button>
                  )}
                </div>

                {/* Card Footer with CTA */}
                <div className="p-6">
                  <Button
                    onClick={() => !isCurrentPlan && handleSubscribe(planId)}
                    disabled={isDisabled || isCurrentPlan}
                    className={cn(
                      'w-full justify-center',
                      isRecommended
                        ? 'bg-linko-light text-white hover:bg-linko-dark'
                        : '',
                      isDisabled
                        ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                        : '',
                      isCurrentPlan
                        ? 'cursor-default border border-green-300 bg-green-50 text-green-700'
                        : '',
                    )}
                  >
                    {isCurrentPlan
                      ? t('current-plan')
                      : planId === 'freemium'
                        ? t('free')
                        : cta}
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Pricing;
